import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ReferenceLine } from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';
import { Scale, ArrowLeftRight, TrendingUp, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function BalanceAnalytics({ childId, childName }) {
  const [testData, setTestData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (childId) {
      fetchTestData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const fetchTestData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [testsResponse, analyticsResponse] = await Promise.all([
        axios.get(`${API}/children/${childId}/athletic-tests`, { headers }),
        axios.get(`${API}/analytics/balance/${childId}`, { headers }).catch(() => null)
      ]);
      
      setTestData(testsResponse.data);
      if (analyticsResponse?.data) {
        setAnalyticsData(analyticsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching test data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate 5-0-5 balance data
  const agilityBalance = useMemo(() => {
    const leftTests = testData.filter(t => t.test_name === '5-0-5 (Left)');
    const rightTests = testData.filter(t => t.test_name === '5-0-5 (Right)');
    
    if (leftTests.length === 0 && rightTests.length === 0) return null;

    const latestLeft = leftTests.sort((a, b) => new Date(b.test_date) - new Date(a.test_date))[0];
    const latestRight = rightTests.sort((a, b) => new Date(b.test_date) - new Date(a.test_date))[0];

    if (!latestLeft || !latestRight) return null;

    const leftTime = latestLeft.result_value;
    const rightTime = latestRight.result_value;
    const diff = Math.abs(leftTime - rightTime);
    const percentDiff = ((diff / Math.min(leftTime, rightTime)) * 100).toFixed(1);
    const preference = leftTime < rightTime ? 'Left' : rightTime < leftTime ? 'Right' : 'Balanced';
    
    // Historical data for trend
    const historical = [];
    const dates = [...new Set([...leftTests, ...rightTests].map(t => t.test_date.split('T')[0]))].sort();
    
    dates.forEach(date => {
      const left = leftTests.find(t => t.test_date.split('T')[0] === date);
      const right = rightTests.find(t => t.test_date.split('T')[0] === date);
      if (left && right) {
        historical.push({
          date: new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
          left: left.result_value,
          right: right.result_value,
          asymmetry: (((left.result_value - right.result_value) / Math.min(left.result_value, right.result_value)) * 100).toFixed(1)
        });
      }
    });

    return {
      left: leftTime,
      right: rightTime,
      leftGrade: latestLeft.grade,
      rightGrade: latestRight.grade,
      diff,
      percentDiff,
      preference,
      isBalanced: percentDiff < 10,
      historical
    };
  }, [testData]);

  // Calculate Single Leg Jump balance data
  const jumpBalance = useMemo(() => {
    const leftTests = testData.filter(t => t.test_name === 'Single Leg Jump (Left)');
    const rightTests = testData.filter(t => t.test_name === 'Single Leg Jump (Right)');
    
    if (leftTests.length === 0 && rightTests.length === 0) return null;

    const latestLeft = leftTests.sort((a, b) => new Date(b.test_date) - new Date(a.test_date))[0];
    const latestRight = rightTests.sort((a, b) => new Date(b.test_date) - new Date(a.test_date))[0];

    if (!latestLeft || !latestRight) return null;

    const leftHeight = latestLeft.result_value;
    const rightHeight = latestRight.result_value;
    const diff = Math.abs(leftHeight - rightHeight);
    const percentDiff = ((diff / Math.max(leftHeight, rightHeight)) * 100).toFixed(1);
    const preference = leftHeight > rightHeight ? 'Left' : rightHeight > leftHeight ? 'Right' : 'Balanced';
    
    // Historical data
    const historical = [];
    const dates = [...new Set([...leftTests, ...rightTests].map(t => t.test_date.split('T')[0]))].sort();
    
    dates.forEach(date => {
      const left = leftTests.find(t => t.test_date.split('T')[0] === date);
      const right = rightTests.find(t => t.test_date.split('T')[0] === date);
      if (left && right) {
        historical.push({
          date: new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
          left: left.result_value,
          right: right.result_value,
          asymmetry: (((left.result_value - right.result_value) / Math.max(left.result_value, right.result_value)) * 100).toFixed(1)
        });
      }
    });

    return {
      left: leftHeight,
      right: rightHeight,
      leftGrade: latestLeft.grade,
      rightGrade: latestRight.grade,
      diff,
      percentDiff,
      preference,
      isBalanced: percentDiff < 15,
      historical
    };
  }, [testData]);

  // Radar chart data for overall balance profile
  const balanceProfile = useMemo(() => {
    const data = [];
    
    if (agilityBalance) {
      const avgTime = (agilityBalance.left + agilityBalance.right) / 2;
      const leftScore = Math.max(0, 100 - (agilityBalance.left - 2) * 50);
      const rightScore = Math.max(0, 100 - (agilityBalance.right - 2) * 50);
      data.push(
        { metric: '5-0-5 Left', value: leftScore, fullMark: 100 },
        { metric: '5-0-5 Right', value: rightScore, fullMark: 100 }
      );
    }
    
    if (jumpBalance) {
      const maxJump = Math.max(jumpBalance.left, jumpBalance.right);
      data.push(
        { metric: 'SL Jump Left', value: (jumpBalance.left / maxJump) * 100, fullMark: 100 },
        { metric: 'SL Jump Right', value: (jumpBalance.right / maxJump) * 100, fullMark: 100 }
      );
    }
    
    return data;
  }, [agilityBalance, jumpBalance]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#02b5ff]"></div>
      </div>
    );
  }

  if (!agilityBalance && !jumpBalance) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <Scale className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No bilateral comparison data available</p>
        <p className="text-xs mt-1">Requires both left and right test results</p>
      </div>
    );
  }

  const getBalanceColor = (percentDiff, isTimeBased = false) => {
    const threshold = isTimeBased ? 10 : 15;
    if (percentDiff < threshold / 2) return 'text-emerald-400';
    if (percentDiff < threshold) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getBalanceStatus = (percentDiff, isTimeBased = false) => {
    const threshold = isTimeBased ? 10 : 15;
    if (percentDiff < threshold / 2) return { icon: CheckCircle, text: 'Excellent Balance', color: 'emerald' };
    if (percentDiff < threshold) return { icon: AlertTriangle, text: 'Minor Imbalance', color: 'yellow' };
    return { icon: AlertTriangle, text: 'Significant Imbalance', color: 'red' };
  };

  const getGradeLabel = (grade) => {
    const labels = { 5: 'Excellent', 4: 'Good', 3: 'Average', 2: 'Below Avg', 1: 'Needs Work' };
    return labels[grade] || 'N/A';
  };

  const getGradeColor = (grade) => {
    if (grade >= 4) return 'text-emerald-400';
    if (grade === 3) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-6">
      {/* Age Group & Benchmark Info Banner */}
      {analyticsData && (
        <div className={`rounded-xl p-4 border ${analyticsData.is_dob_default ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#02b5ff]/10 border-[#02b5ff]/30'}`}>
          <div className="flex items-center gap-3">
            {analyticsData.is_dob_default ? (
              <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" />
            ) : (
              <Info className="h-5 w-5 text-[#02b5ff] flex-shrink-0" />
            )}
            <div>
              <p className={`text-sm font-medium ${analyticsData.is_dob_default ? 'text-amber-400' : 'text-[#02b5ff]'}`}>
                Age-Based Benchmarks: {analyticsData.age_group} years
                {analyticsData.is_dob_default && ' (Default DOB - Update for accurate benchmarks)'}
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                Current age: {analyticsData.current_age} years • Asymmetry &gt;10% (agility) or &gt;15% (jump) indicates imbalance
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 5-0-5 Agility Balance */}
      {agilityBalance && (
        <div className="bg-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-orange-400" />
              <h3 className="text-lg font-bold text-white">5-0-5 Agility Balance</h3>
            </div>
            {(() => {
              const status = getBalanceStatus(agilityBalance.percentDiff, true);
              const StatusIcon = status.icon;
              return (
                <div className={`flex items-center gap-1 text-${status.color}-400`}>
                  <StatusIcon className="h-4 w-4" />
                  <span className="text-sm">{status.text}</span>
                </div>
              );
            })()}
          </div>

          {/* Balance Comparison Bars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              {/* Left Side */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Left Turn</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${agilityBalance.preference === 'Left' ? 'text-emerald-400' : 'text-zinc-300'}`}>
                      {agilityBalance.left.toFixed(2)}s
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getGradeColor(agilityBalance.leftGrade)} bg-zinc-900`}>
                      {getGradeLabel(agilityBalance.leftGrade)}
                    </span>
                  </div>
                </div>
                <div className="h-4 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (2.5 / agilityBalance.left) * 100)}%` }}
                  />
                </div>
              </div>
              
              {/* Right Side */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Right Turn</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${agilityBalance.preference === 'Right' ? 'text-emerald-400' : 'text-zinc-300'}`}>
                      {agilityBalance.right.toFixed(2)}s
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getGradeColor(agilityBalance.rightGrade)} bg-zinc-900`}>
                      {getGradeLabel(agilityBalance.rightGrade)}
                    </span>
                  </div>
                </div>
                <div className="h-4 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (2.5 / agilityBalance.right) * 100)}%` }}
                  />
                </div>
              </div>

              {/* Age Benchmark Reference */}
              {analyticsData?.benchmarks?.['5-0-5'] && (
                <div className="mt-4 p-3 bg-zinc-900/50 rounded-lg">
                  <div className="text-xs font-medium text-zinc-400 mb-2">Age Group Benchmark ({analyticsData.age_group})</div>
                  <div className="flex items-center gap-4 text-xs">
                    <div>
                      <span className="text-emerald-400">Excellent:</span>
                      <span className="text-zinc-300 ml-1">&lt;{analyticsData.benchmarks['5-0-5'].grade_5_threshold}s</span>
                    </div>
                    <div>
                      <span className="text-yellow-400">Asymmetry Alert:</span>
                      <span className="text-zinc-300 ml-1">&gt;{analyticsData.benchmarks['5-0-5'].asymmetry_warning_threshold}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Balance Indicator */}
            <div className="flex flex-col items-center justify-center">
              <div className={`text-4xl font-bold ${getBalanceColor(agilityBalance.percentDiff, true)}`}>
                {agilityBalance.percentDiff}%
              </div>
              <div className="text-sm text-zinc-400">Asymmetry</div>
              <div className="mt-2 text-sm">
                <span className="text-zinc-400">Preference: </span>
                <span className={`font-bold ${
                  agilityBalance.preference === 'Left' ? 'text-blue-400' : 
                  agilityBalance.preference === 'Right' ? 'text-orange-400' : 'text-emerald-400'
                }`}>
                  {agilityBalance.preference} {agilityBalance.preference !== 'Balanced' && `(${agilityBalance.diff.toFixed(2)}s faster)`}
                </span>
              </div>
            </div>
          </div>

          {/* Historical Trend */}
          {agilityBalance.historical.length > 1 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Balance Trend Over Time
              </h4>
              <div className="h-48 min-h-[192px]">
                <SafeResponsiveContainer width="100%" height="100%" minHeight={192}>
                  <BarChart data={agilityBalance.historical} barGap={0}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" domain={['dataMin - 0.1', 'dataMax + 0.1']} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value, name) => [`${value}s`, name === 'left' ? 'Left Turn' : 'Right Turn']}
                    />
                    <Legend />
                    {/* Benchmark reference line */}
                    {analyticsData?.benchmarks?.['5-0-5']?.grade_5_threshold && (
                      <ReferenceLine 
                        y={analyticsData.benchmarks['5-0-5'].grade_5_threshold} 
                        stroke="#10b981" 
                        strokeDasharray="5 5"
                        label={{ value: 'Excellent', fill: '#10b981', fontSize: 10 }}
                      />
                    )}
                    <Bar dataKey="left" name="Left Turn" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="right" name="Right Turn" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </SafeResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Single Leg Jump Balance */}
      {jumpBalance && (
        <div className="bg-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-purple-400" />
              <h3 className="text-lg font-bold text-white">Single Leg Jump Balance</h3>
            </div>
            {(() => {
              const status = getBalanceStatus(jumpBalance.percentDiff, false);
              const StatusIcon = status.icon;
              return (
                <div className={`flex items-center gap-1 text-${status.color}-400`}>
                  <StatusIcon className="h-4 w-4" />
                  <span className="text-sm">{status.text}</span>
                </div>
              );
            })()}
          </div>

          {/* Balance Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              {/* Left Leg */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Left Leg</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${jumpBalance.preference === 'Left' ? 'text-emerald-400' : 'text-zinc-300'}`}>
                      {jumpBalance.left.toFixed(1)} cm
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getGradeColor(jumpBalance.leftGrade)} bg-zinc-900`}>
                      {getGradeLabel(jumpBalance.leftGrade)}
                    </span>
                  </div>
                </div>
                <div className="h-4 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all"
                    style={{ width: `${(jumpBalance.left / Math.max(jumpBalance.left, jumpBalance.right)) * 100}%` }}
                  />
                </div>
              </div>
              
              {/* Right Leg */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Right Leg</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${jumpBalance.preference === 'Right' ? 'text-emerald-400' : 'text-zinc-300'}`}>
                      {jumpBalance.right.toFixed(1)} cm
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getGradeColor(jumpBalance.rightGrade)} bg-zinc-900`}>
                      {getGradeLabel(jumpBalance.rightGrade)}
                    </span>
                  </div>
                </div>
                <div className="h-4 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-pink-500 to-pink-400 rounded-full transition-all"
                    style={{ width: `${(jumpBalance.right / Math.max(jumpBalance.left, jumpBalance.right)) * 100}%` }}
                  />
                </div>
              </div>

              {/* Age Benchmark Reference */}
              {analyticsData?.benchmarks?.['Single Leg Jump'] && (
                <div className="mt-4 p-3 bg-zinc-900/50 rounded-lg">
                  <div className="text-xs font-medium text-zinc-400 mb-2">Age Group Benchmark ({analyticsData.age_group})</div>
                  <div className="flex items-center gap-4 text-xs">
                    <div>
                      <span className="text-emerald-400">Excellent:</span>
                      <span className="text-zinc-300 ml-1">&gt;{analyticsData.benchmarks['Single Leg Jump'].grade_5_threshold}cm</span>
                    </div>
                    <div>
                      <span className="text-yellow-400">Asymmetry Alert:</span>
                      <span className="text-zinc-300 ml-1">&gt;{analyticsData.benchmarks['Single Leg Jump'].asymmetry_warning_threshold}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Balance Indicator */}
            <div className="flex flex-col items-center justify-center">
              <div className={`text-4xl font-bold ${getBalanceColor(jumpBalance.percentDiff, false)}`}>
                {jumpBalance.percentDiff}%
              </div>
              <div className="text-sm text-zinc-400">Asymmetry</div>
              <div className="mt-2 text-sm">
                <span className="text-zinc-400">Stronger: </span>
                <span className={`font-bold ${
                  jumpBalance.preference === 'Left' ? 'text-purple-400' : 
                  jumpBalance.preference === 'Right' ? 'text-pink-400' : 'text-emerald-400'
                }`}>
                  {jumpBalance.preference} {jumpBalance.preference !== 'Balanced' && `(+${jumpBalance.diff.toFixed(1)}cm)`}
                </span>
              </div>
            </div>
          </div>

          {/* Historical Trend */}
          {jumpBalance.historical.length > 1 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Jump Height Trend Over Time
              </h4>
              <div className="h-48 min-h-[192px]">
                <SafeResponsiveContainer width="100%" height="100%" minHeight={192}>
                  <BarChart data={jumpBalance.historical} barGap={0}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value, name) => [`${value} cm`, name === 'left' ? 'Left Leg' : 'Right Leg']}
                    />
                    <Legend />
                    {/* Benchmark reference line */}
                    {analyticsData?.benchmarks?.['Single Leg Jump']?.grade_5_threshold && (
                      <ReferenceLine 
                        y={analyticsData.benchmarks['Single Leg Jump'].grade_5_threshold} 
                        stroke="#10b981" 
                        strokeDasharray="5 5"
                        label={{ value: 'Excellent', fill: '#10b981', fontSize: 10 }}
                      />
                    )}
                    <Bar dataKey="left" name="Left Leg" fill="#a855f7" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="right" name="Right Leg" fill="#ec4899" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </SafeResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overall Balance Profile Radar */}
      {balanceProfile.length >= 4 && (
        <div className="bg-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="h-5 w-5 text-[#02b5ff]" />
            <h3 className="text-lg font-bold text-white">Overall Balance Profile</h3>
          </div>
          <div className="h-64">
            <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
              <RadarChart data={balanceProfile}>
                <PolarGrid stroke="#374151" />
                <PolarAngleAxis dataKey="metric" stroke="#9ca3af" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#9ca3af" />
                <Radar
                  name="Performance"
                  dataKey="value"
                  stroke="#02b5ff"
                  fill="#02b5ff"
                  fillOpacity={0.3}
                />
              </RadarChart>
            </SafeResponsiveContainer>
          </div>
          <p className="text-xs text-zinc-500 text-center mt-2">
            Higher values indicate better performance. Balanced athletes show symmetrical profiles.
          </p>
        </div>
      )}
    </div>
  );
}
